export type TenantStatus = 'active' | 'inactive' | 'suspended' | 'trial';

export type TenantPlan = 'free' | 'starter' | 'professional' | 'enterprise';

export interface Tenant {
  id: string;
  name: string;
  domain: string | null;
  status: TenantStatus;
  plan: TenantPlan;
  preferredLlm: string | null;
  features: TenantFeatures;
  settings: TenantSettings;
  createdAt: string;
  updatedAt: string;
}

// Module system
export type TenantModuleKey = 'research' | 'accounting_docs' | 'intake' | 'image_intelligence' | 'global_knowledge';

export interface TenantModules {
  research: boolean;
  accounting_docs: boolean;
  intake: boolean;
  image_intelligence: boolean;
  global_knowledge: boolean;
}

export const DEFAULT_TENANT_MODULES: TenantModules = {
  research: false,
  accounting_docs: false,
  intake: false,
  image_intelligence: false,
  global_knowledge: false,
};

export const MODULE_METADATA: Record<TenantModuleKey, { label: string; description: string }> = {
  research: {
    label: 'Research Intelligence',
    description: 'Research resources, researchers, institutions, analytics, citations, and more',
  },
  accounting_docs: {
    label: 'ATO / Accounting Docs',
    description: 'Access to the ATO Knowledge Base for accounting document queries',
  },
  intake: {
    label: 'Intake Automation',
    description: 'Client intake management: clients, document types, FAQs',
  },
  image_intelligence: {
    label: 'Image Intelligence',
    description: 'Image Search page and Extract Images in documents list',
  },
  global_knowledge: {
    label: 'Global Knowledge Base',
    description: 'Access to the shared Global Knowledge Base across the platform',
  },
};

export interface TenantFeatures {
  maxUsers: number;
  maxStorage: number;
  voiceEnabled: boolean;
  apiAccess: boolean;
  customRoles: boolean;
  advancedReports: boolean;
  integrations: string[];
}

export interface TenantSettings {
  timezone: string;
  language: string;
  dateFormat: string;
  currency: string;
}

export interface TenantStats {
  totalUsers: number;
  activeUsers: number;
  storageUsed: number; // in GB
  apiCalls: number; // this month
  lastActivityAt: string | null;
}

export interface TenantWithStats extends Tenant {
  stats: TenantStats;
}

export interface CreateTenantRequest {
  name: string;
  domain?: string;
  plan: TenantPlan;
  adminEmail: string;
  adminFirstName?: string;
  adminLastName?: string;
}

export interface UpdateTenantRequest {
  name?: string;
  domain?: string;
  status?: TenantStatus;
  plan?: TenantPlan;
  preferredLlm?: string;
  features?: Partial<TenantFeatures>;
  settings?: Partial<TenantSettings>;
}
